import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { OptionType } from "@/types/OptionType";
import { IRequest } from "@/interfaces/IRequest";
import { merge } from "lodash";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { DeliveryNoteDto } from "@/core/features/delivery-notes/delivery-note.dto";
import { CreateDeliveryNoteDto } from "@/core/features/delivery-notes/create-delivery-note.dto";
import { UpdateDeliveryNoteDto } from "@/core/features/delivery-notes/update-delivery-note.dto";
import { DeliveryNotesTotalDto } from "@/core/features/delivery-notes/delivery-notes-total.dto";
import { formatAppDate } from "@/utils/dates/dateFormats";
import { ChangeDeliveryNoteStatusDto } from "@/core/features/delivery-notes/change-delivery-note-status.dto";
import { DeliveryNoteStatusEnum } from "@altertec_gparn/lib";
import { CreateReturnDeliveryNoteDto } from "@/core/features/delivery-notes/create-return-delivery-note.dto";
import { CreateFileDto } from "@/core/features/file/create-file.dto";
import { DeleteFileDto } from "@/core/features/file/delete-file.dto";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";
import { AddPurchaseOrdersToDeliveryNoteDto } from "@/core/features/delivery-notes/add-purchase-orders-to-delivery-note.dto";

export class DeliveryNotesResource extends ResourceBaseService<
  DeliveryNoteDto,
  CreateDeliveryNoteDto,
  UpdateDeliveryNoteDto
> {
  protected EntityDto = DeliveryNoteDto;
  protected CreateEntityDto = CreateDeliveryNoteDto;
  protected UpdateEntityDto = UpdateDeliveryNoteDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/delivery-notes");
  }

  async optionLoader(inputValue: string, customRequest: IRequest = {}): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "code"] } : {};
    request.sortBy = "createdAt";
    const { data } = await this.findAll(applyOptionLoaderPagination(merge(customRequest, request)));
    return data.map((value) => value.mapEntityToOptionType([value.code, formatAppDate(value.createdAt)]));
  }

  async getTotal(request: IRequest): Promise<DeliveryNotesTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(DeliveryNotesTotalDto, result));
  }

  changeStatus(id: string, status: DeliveryNoteStatusEnum): Promise<{ jobId?: string }> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/change-status",
        method: "PATCH",
        auth: true,
        body: plainToInstance(ChangeDeliveryNoteStatusDto, { status: status, id: id }),
      })
    );
  }

  createReturn(dto: CreateReturnDeliveryNoteDto): Promise<DeliveryNoteDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/return",
        method: "POST",
        auth: true,
        body: plainToInstance(CreateReturnDeliveryNoteDto, dto),
      })
    );
  }

  createManyAttachments(id: string, files: CreateFileDto[]): Promise<DeliveryNoteDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "POST",
        auth: true,
        body: { bulk: files },
      })
    );
  }

  deleteAttachment(id: string, deleteFile: DeleteFileDto): Promise<DeliveryNoteDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "DELETE",
        auth: true,
        body: deleteFile,
      })
    );
  }

  addPurchaseOrders(id: string, dto: AddPurchaseOrdersToDeliveryNoteDto): Promise<DeliveryNoteDto> {
    return this.apiHttpClient
      .request<AddPurchaseOrdersToDeliveryNoteDto, DeliveryNoteDto>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/purchase-orders",
          method: "POST",
          auth: true,
          body: plainToInstance(AddPurchaseOrdersToDeliveryNoteDto, dto, { excludeExtraneousValues: true }),
        })
      )
      .then((deliveryNote) => plainToInstance(DeliveryNoteDto, deliveryNote));
  }

  removePurchaseOrder(id: string, purchaseOrderId: string): Promise<void> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/purchase-orders/" + purchaseOrderId,
        method: "DELETE",
        auth: true,
      })
    );
  }

  retrySapSync(id: string): Promise<DeliveryNoteDto> {
    return this.apiHttpClient
      .request<null, DeliveryNoteDto>(
        HttpRequest.create({
          url: this.resourceUrl + "/" + id + "/retry-sap-sync",
          method: "POST",
          auth: true,
        })
      )
      .then((deliveryNote) => plainToInstance(DeliveryNoteDto, deliveryNote));
  }
}
